import { PERMISSIONS } from '@ibe/constants';
import { ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Trash2, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { useSoftDeleteTenant } from '../api/mutations.js';
import { type TenantRow } from '../api/schemas.js';

export const tenantColumns: readonly ColumnDescriptor<TenantRow>[] = [
  {
    id: 'name',
    header: 'Tenant',
    sortable: true,
    alwaysVisible: true,
    // Two fields, one column: a tenant is known by name and slug, and a
    // separate slug column costs width the filters need.
    fields: ['name', 'slug'],
    // Every field here is optional: the reader's column choice decides what
    // the server projects. This column is alwaysVisible so name and slug are
    // always requested, but falling back to the id beats rendering a row whose
    // link has no text if a field ever goes missing.
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/tenants/${row.id}`}
        secondary={row.slug ?? null}
      />
    ),
  },
  {
    id: 'timezone',
    header: 'Timezone',
    sortable: true,
    fields: ['timezone'],
    cell: (row) => row.timezone,
  },

  {
    id: 'enabledModules',
    header: 'Modules',
    fields: ['enabledModules'],
    // Off by default: informative on a tenant page, noise in a wide table
    // where the filter answers the same question in less space.
    defaultVisible: false,
    cell: (row) => (row.enabledModules ?? []).join(', '),
  },
  {
    id: 'status',
    header: 'Status',
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'actions',
    header: '',
    // An icon-only header reads as nothing in a screen reader's table
    // navigation, so it gets an explicit name.
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    // Declares `name` although it renders no visible text: the action labels
    // name the row for a screen reader, so the column reads the field even
    // though it does not show it. With `fields: []` and projection now real,
    // hiding the Tenant column made every action announce "View undefined".
    fields: ['name'],
    cell: (row: TenantRow): React.JSX.Element => <TenantRowActions row={row} />,
  },
];

/**
 * The per-row controls, as a component rather than inline markup.
 *
 * Deleting needs a mutation, a confirmation and a permission check, and a
 * column descriptor's `cell` is a plain function — it cannot hold hooks. This
 * is the smallest thing that can.
 */
function TenantRowActions({ row }: { readonly row: TenantRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteTenant();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/tenants/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/tenants/${row.id}/edit` },
            {
              label: `View administrators of ${label}`,
              icon: Users,
              to: `/tenant-admins?tenantId=${encodeURIComponent(row.id)}`,
            },
          ]}
        />
        {/* Offered only to someone who may actually manage tenant users. */}
        <PermissionGuard permission={PERMISSIONS.TENANT_USER_MANAGE}>
          <RowActions
            actions={[
              {
                label: `Create administrator for ${label}`,
                icon: UserPlus,
                to: `/tenant-admins/new?tenantId=${encodeURIComponent(row.id)}`,
              },
            ]}
          />
        </PermissionGuard>
        {/* Offered only to someone who may actually delete. A control that
            always answers 403 teaches people to distrust every other button. */}
        <PermissionGuard permission={PERMISSIONS.TENANT_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete tenant"
        // Says what actually happens. "Delete" alone reads as irreversible,
        // and an operator who believes that will not ask for it back.
        description={`${label} will be archived and removed from this list. Its data is retained, not erased.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}
